import { ed25519 } from "@noble/curves/ed25519";
import bs58 from "bs58";

export class Ed25519 {
	/**
	 * Derive a Solana public key from a private key
	 * @param privateKeyBase58orArray - Base58-encoded private key (64 bytes Solana format) or Uint8Array
	 * @returns Base58-encoded public key
	 */
	getPublicKey(privateKeyBase58orArray: string | Uint8Array): string {
		const keyBytes =
			typeof privateKeyBase58orArray === "string"
				? bs58.decode(privateKeyBase58orArray)
				: privateKeyBase58orArray;

		// Solana keypairs are 64 bytes: first 32 bytes are the private key, last 32 bytes are the public key
		if (keyBytes.length === 64) {
			// Return the public key portion directly
			return bs58.encode(keyBytes.slice(32, 64));
		}

		if (keyBytes.length === 32) {
			// If it's already a 32-byte private key, derive the public key
			const publicKeyBytes = ed25519.getPublicKey(keyBytes);
			return bs58.encode(publicKeyBytes);
		}

		throw new Error(
			`Invalid key length: ${keyBytes.length}. Expected 32 or 64 bytes.`,
		);
	}

	/**
	 * Get a secret key from a seed
	 * @param seed - Seed
	 * @returns Secret key
	 */
	secretKeyFromSeed(seed: Uint8Array): Uint8Array {
		if (seed.length < 32) {
			throw new Error(
				`Invalid seed length: ${seed.length}. Expected at least 32 bytes.`,
			);
		}
		const trimmedSeed = seed.slice(0, 32);
		const publicKey = ed25519.getPublicKey(trimmedSeed);
		return this.concatBytes(trimmedSeed, publicKey);
	}

	/**
	 * Sign a message with a private key using ed25519
	 * @param payload - Message to sign (Uint8Array or utf-8 string)
	 * @param privateKey - Private key
	 * @returns Signature
	 */
	sign(
		payload: Uint8Array | string,
		privateKey: Uint8Array | string,
	): Uint8Array {
		const messageBytes =
			typeof payload === "string" ? bs58.decode(payload) : payload;

		const keyBytes =
			typeof privateKey === "string" ? bs58.decode(privateKey) : privateKey;

		// Extract the private key portion (first 32 bytes) for Solana keypairs
		let privateKeyBytes: Uint8Array;
		if (keyBytes.length === 32) {
			privateKeyBytes = keyBytes;
		} else if (keyBytes.length === 64) {
			privateKeyBytes = keyBytes.slice(0, 32);
		} else {
			throw new Error(
				`Invalid private key length: ${keyBytes.length}. Expected 32 bytes.`,
			);
		}

		const signatureBytes = ed25519.sign(messageBytes, privateKeyBytes);
		return signatureBytes;
	}

	private concatBytes(...arrays: Uint8Array[]): Uint8Array {
		let totalLength = 0;
		for (const arr of arrays) {
			totalLength += arr.length;
		}
		const result = new Uint8Array(totalLength);
		let offset = 0;
		for (const arr of arrays) {
			result.set(arr, offset);
			offset += arr.length;
		}
		return result;
	}
}
